import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Stack } from '@adc/parallax-component-library';
import { useForm, useStore } from '@tanstack/react-form';
import * as z from 'zod';

import { CLMPhoneInput } from '~/components/inputs/CLMPhoneNumberInput';
import { AlertPreview } from '~/features/department/components/AlertPreview';
import { buildTestId } from '~/utils/buildTestId';
import { getErrorProps } from '~/utils/forms/getErrorProps';

import { departmentAlertSchema } from './validations';

export type DepartmentAlertFormValues = z.infer<typeof departmentAlertSchema>;

export type DepartmentAlertFormRef = {
  submitForm: () => Promise<void>;
};

type Props = {
  testID: string;
  onSubmit: (values: DepartmentAlertFormValues) => Promise<void>;
  defaultValues: Partial<DepartmentAlertFormValues>;
  hasFormChanged?: (isDirty: boolean) => void;
};

export const DepartmentAlertForm = forwardRef<DepartmentAlertFormRef, Props>(
  ({ testID, onSubmit, defaultValues, hasFormChanged }, ref) => {
    const form = useForm({
      defaultValues: {
        alertPhone: defaultValues.alertPhone,
      },
      validators: {
        onMount: departmentAlertSchema,
        onChange: departmentAlertSchema,
      },
      onSubmit: ({ value }) => {
        return onSubmit(departmentAlertSchema.parse(value));
      },
    });

    const [isDirty, alertPhone] = useStore(form.store, (state) => [
      state.isDirty,
      state.values.alertPhone,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        submitForm: form.handleSubmit,
      }),
      [form.handleSubmit],
    );

    useEffect(() => {
      if (hasFormChanged) {
        hasFormChanged(isDirty);
      }
    }, [isDirty, hasFormChanged]);

    return (
      <Stack gap="$6" paddingTop="$2" testID={testID}>
        <Stack flex={1}>
          <form.Field name="alertPhone">
            {(field) => (
              <CLMPhoneInput
                label="Number to call"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                testID={buildTestId({
                  testID,
                  suffix: ['alert', 'phone'],
                })}
                {...getErrorProps(field)}
              />
            )}
          </form.Field>

          <Stack justifyContent="center" alignItems="center" marginTop="$8">
            <AlertPreview
              testID={buildTestId({ testID, suffix: ['alert', 'preview'] })}
              phone={alertPhone ?? ''}
            />
          </Stack>
        </Stack>
      </Stack>
    );
  },
);

DepartmentAlertForm.displayName = 'DepartmentAlertForm';
